import { AfterViewInit, Component, Input, OnChanges, OnDestroy, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { catchError, forkJoin, map, Observable } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { GasLiftValve } from 'src/app/perical/models/gasLiftValve.model';
import { GasLiftOperation, OperationType } from 'src/app/perical/models/thermal-operation.model';
import { ThermalOperationsService } from 'src/app/perical/services/thermal-operations.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';

export interface GasLiftOperationForm {
  type: OperationType;
  fluid: Fluid;
  gasLiftValve: GasLiftValve;
  injectionPressure: number;
  inletTemperature: number;
  gasFlowRate: number;
  annulusIndex: number;
}

@Component({
    selector: 'app-gas-lift-details',
    templateUrl: './gas-lift-details.component.html',
    styles: [``],
    providers: subformComponentProviders(GasLiftDetailsComponent),
    standalone: false
})
export class GasLiftDetailsComponent implements AfterViewInit, OnChanges, OnDestroy {

  constructor(
    private _packerService: PackerService,
    private _thermalOperationsService: ThermalOperationsService,
    private _signalRService: SignalRService) {}

  public fluids: Fluid[];
  @Input('fluids') set _fluids(fluids: Fluid[]) {
    this.fluids = fluids?.filter(f => f.state.type === FluidType.BLACKOIL && f.state.oilApiGravity == 0) ?? [];
  }

  @Input()
  public currentString: any;

  @Input()
  public surfaceTemperature: number;

  @Input()
  public userUnits: UserUnitsModel;

  public FluidType: typeof FluidType = FluidType;
  public isLoading = signal(false);
  public tempUnit: string;
  public depthUnit: string;
  public pressureUnit: string;
  public gasFlowUnit: string;
  public temperatureValidation: { min: number, max: number };
  public pressureValidation: { min: number, max: number };
  public gasLiftValves: GasLiftValve[];

  public form = createForm<GasLiftOperation, GasLiftOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.GASLIFT),
      fluid: new UntypedFormControl(null, [Validators.required]),
      gasLiftValve: new UntypedFormControl(null, [Validators.required]),
      injectionPressure: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      gasFlowRate: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      annulusIndex: new UntypedFormControl(null)
    },
    toFormGroup: (obj: GasLiftOperation | null): GasLiftOperationForm | null => {
      if (!obj) {
        return null;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fluid, flow, ...commonValues } = obj;

      const selectedFluid = this.fluids.find(f => f.id === obj.fluid.id);
      const legacyGasRate = (obj as any)['gasRate'];
      const gasFlowRate = (flow as any)?.gasFlowRate ?? legacyGasRate ?? null;

      return {
        fluid: selectedFluid,
        gasLiftValve: obj.gasLiftValve,
        injectionPressure: obj.injectionPressure,
        inletTemperature: obj.inletTemperature,
        gasFlowRate,
        type: obj.type,
        ...commonValues
      };
    },
    fromFormGroup: (formValue: GasLiftOperationForm): GasLiftOperation | null => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fluid, type, gasFlowRate, injectionPressure, inletTemperature, ...commonValues } = formValue;

      if (injectionPressure == null || inletTemperature == null || gasFlowRate == null) {
        return null;
      }

      return {
        ...commonValues,
        type: OperationType.GASLIFT,
        fluid: fluid,
        gasLiftValve: formValue.gasLiftValve,
        injectionPressure: formValue.injectionPressure,
        inletTemperature: formValue.inletTemperature,
        flow: {
          co2EosOption: 'GERG2008',
          gasFlowRate: gasFlowRate ?? 0,
          gasModel: 'srk',
          gasToOilRatio: 'gasWater',
          multiphaseCorrelation: 'beggsBrill',
          oilFlowRate: 0,
          pipeRoughness: 0.001968,
          type: 'hydrocarbonFlow',
          waterFlowRate: 0
        } as any
      };
    },
  });

  ngAfterViewInit() {
    const defaultFluid = this.fluids[0];
    if (!this.form.formGroup.controls.fluid.value && defaultFluid) {
      this.form.formGroup.controls.fluid.setValue(defaultFluid);
    }

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getGasLiftValves();
  }

  private signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_GLV) {
      this.getGasLiftValves();
    }
  }

  async ngOnChanges(): Promise<void> {
    this.tempUnit = this.userUnits.temperature;
    this.pressureUnit = unitsLib[this.userUnits.pressure].symbol;
    this.gasFlowUnit = unitsLib[this.userUnits.gasInjectionRate].symbol;
    this.depthUnit = this.userUnits.longLengths;

    switch (this.userUnits.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 100000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 6894 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 689475 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 6804 };
        break;
    }

    this.form.formGroup.controls.injectionPressure.setValidators([Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max), Validators.required]);

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: -70, max: 1000 };
        break;
      case '°C':
        this.temperatureValidation = { min: -56.66, max: 537.77 };
        break;
      case 'K':
        this.temperatureValidation = { min: 216.48, max: 810.92 };
        break;
    }
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max), Validators.required]);
  }

  private getGasLiftValves() {
    this.isLoading.set(true);
    const sources: Observable<any>[] = [
      this._packerService.getPackersForTubular() as Observable<any>,
      this._thermalOperationsService.getGasLiftValves() as Observable<any>
    ];

    forkJoin(sources).pipe(
      map(([packers, gasLiftValves]) => {
        const depthFactor = this.depthUnit === 'ft' ? 100 : 30;
        let minDepth = this.currentString.shoeMd;
        if (packers.packers.length > 0) {
          const topPacker = Math.min(...packers.packers.map(p => p.measuredDepth));
          minDepth = topPacker - depthFactor;
        }
          
        this.gasLiftValves = gasLiftValves.filter(x => x.measuredDepth <= minDepth).sort((a, b) => (a.measuredDepth < b.measuredDepth ? -1 : 1));
        if (!this.form.formGroup.controls.gasLiftValve.value && this.gasLiftValves.length > 0) {
          this.form.formGroup.controls.gasLiftValve.setValue(this.gasLiftValves[0]);
        }

        if (!this.form.formGroup.controls.inletTemperature.valid) {
          this.form.formGroup.controls.inletTemperature.setValue(this.surfaceTemperature);
        }
        this.isLoading.set(false);
      }),
    catchError(err => {
      this.isLoading.set(false);
      return err;
    })).subscribe();
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
